<template>
  <el-col :span="24" ref="trxContainer">
    <el-row type="flex" justify="start" align="middle" class="v-padded-s trx-title-container" ref="trxTitleContainer">
      <el-col :span="isSaving && isSaveable ? 6 : 12" class="h-padded left-aligned-col">
        <el-row type="flex" align="middle" justify="start">
          <h2 style="margin: 0; font-weight: 100; display: inline-block;">{{ $route.name === 'NewJe' ? 'JOURNAL ENTRY' : 'PAYROLL ENTRY' }}</h2>
        </el-row>
      </el-col>
      <el-col :span="12" v-if="isSaving && isSaveable">
        <TransactionProgressBar
          :totalRequests="totalRequests"
          :completedRequests="completedRequests"
          :error="(!!failedLines.length)"
        />
      </el-col>
      <DataEntryButtons
        :isSaveable="isSaveable"
        :isOpenable="isOpenable"
        :isBalanced="transactionIsBalanced"
        :isSaving="isSaving"
        :transactionType="transactionType"
        :hasPermission="hasPermission"
        @goBack="goBack"
        @submit="submitForm"
        @otherActions="otherActions"
        @dropdownClick="handleDropdownClick"
      />
    </el-row>
    <el-row type="flex" ref="mockTrxTitleContainer" />
    <el-row justify="start" align="start" type="flex" class="trx-header" ref="trxHeaderContainer">
      <el-col :span="8" style="margin-right: 10px">
        <el-row justify="start" align="middle" type="flex">
          <HeaderFields
            title="Batch"
            :fields="fields.batch"
            :form="form"
            :idPrefix="'a-'"
            :modalOpen="dialogVisible"
            :isSaving="isSaving"
            @formatDateFields="formatDateFields"
            @formChanged="updateForm"
          />
        </el-row>
      </el-col>
      <el-col :span="8" style="margin: 0 10px;">
        <el-row justify="start" align="middle" type="flex">
          <HeaderFields
            title="Transaction Data"
            :fields="fields.jeData"
            :form="form"
            :idPrefix="'b-'"
            :modalOpen="dialogVisible"
            :isSaving="isSaving"
            @formatDateFields="formatDateFields"
            @formChanged="updateForm"
            :key="effectiveDateMandatory"
          />
        </el-row>
      </el-col>
      <el-col :span="8" style="margin-left: 10px;">
        <el-row justify="start" align="middle" type="flex">
          <HeaderFields
            title="Transaction Info"
            :fields="fields.jeInfo"
            :form="form"
            :idPrefix="'c-'"
            :modalOpen="dialogVisible"
            :isSaving="isSaving"
            @formatDateFields="formatDateFields"
            @formChanged="updateForm"
          />
        </el-row>
      </el-col>
    </el-row>
    <el-row v-if="lines" justify="start" align="middle" type="flex" id="trx-lines-container">
      <DistributionLines
        v-if="dataLoaded"
        entityType="je"
        ref="distribution-lines"
        :fields="prLineFields"
        :lines="lines"
        :initLines="20"
        :modalOpen="dialogVisible"
        :reset="resetLines"
        :parentForm="form"
        :readOnly="isSaving"
        :hasEditPermission="hasEditPermission"
        @lineChanged="trackLineChanges"
        @linesHaveBeenChanged="setLinesHaveBeenChanged"
        @formatMoneyFields="formatLineMoneyField"
        @hasBeenReset="linesHaveBeenReset"
        @updateLines="updateLines"
        @mounted="setBatchAttributeLineValues"
      />
      <LinesLoading v-else :distLinesHeight="distLinesHeight" />
    </el-row>
    <el-dialog
      title="Confirm cancellation"
      :visible.sync="dialogVisible"
      size="tiny"
      :close-on-press-escape="true"
      :close-on-click-modal="false">
      <span>Do you wish to save this transaction as a draft?</span>
      <span slot="footer" class="dialog-footer">
        <el-button
          class="exit-modal-btn"
          :disabled="isSaving"
          @click="cancelExit">
          Cancel
        </el-button>
        <el-button
          class="exit-modal-btn"
          type="primary"
          :disabled="isSaving"
          @click="goBack(true)">
          No
        </el-button>
        <el-button
          class="exit-modal-btn"
          type="primary"
          :disabled="isSaving"
          :loading="isSaving"
          @click="submitForm(false, null, true)">
          Yes
        </el-button>
      </span>
    </el-dialog>
    <RouteChangeDialog :visible="showExitWarningDialog" @isVisible="handleExitWarningDialogVisible" @confirm="goBack(true)" />
    <NewBatch :hasCreatePermission="userCanCreateBatch" @populateFieldWithNewModel="populateFieldWithNewModel" />
  </el-col>
</template>

<script>
  /* eslint-disable camelcase */
  import { mapGetters } from 'vuex'
  import DistributionLines from '@/components/DistributionLinesNew1.vue'
  import HeaderFields from '@/components/HeaderFields.vue'
  import NewBatch from '@/components/RouteComponents/Batches/NewBatch.vue'
  import DataEntryButtons from '@/components/DataEntryButtons.vue'
  import RouteChangeDialog from '@/components/RouteChangeDialog.vue'
  import autoNumber from '@/utils/helpers/DataEntryViews/AutoNumber'
  import { isJeBalanced } from '@/utils/helpers/DataEntryViews/IsTransactionBalanced'
  import { checkIfRequirementsMet } from '@/utils/helpers/Validate'
  import { transactionMixins } from '@/Mixins/Transactions'
  import { getSimpleDate } from '@/utils/helpers/FormatDate'
  import { transactionCreateViewMixins } from '@/Mixins/TransactionCreateViews'
  import LinesLoading from '@/components/LinesLoading.vue'
  import TransactionProgressBar from '@/components/TransactionProgressBar.vue'
  import store from '@/store/store'

  export default {
    components: {
      DistributionLines,
      HeaderFields,
      NewBatch,
      DataEntryButtons,
      RouteChangeDialog,
      TransactionProgressBar,
      LinesLoading
    },
    mixins: [ transactionMixins, transactionCreateViewMixins ],
    data () {
      const batch = this.setFormBatch()
      const sourcecode = this.setPrSourcecode()
      return {
        transactionType: { name: 'je', path: '/jes/', prettyName: 'Payroll' },
        form: {
          batch: { label: batch ? batch.name : '', value: batch ? batch.id : '' },
          period: '',
          description: '',
          date: '',
          number: autoNumber() || '',
          debits_decimal: '0.00',
          credits_decimal: '0.00',
          confirmation_number: '',
          original_trx_number: '',
          effective_date: '',
          source_code: this.$route.name === 'NewJe' ? { label: '', value: '' } : { label: sourcecode.name, value: sourcecode.id },
          lines: []
        },
        effectiveDateMandatory: false,
        openPoLines: [],
        notificationBox: undefined
      }
    },
    methods: {
      /**
       * Returns the source code model corresponding to the source code where 'code' === 'PR'
       */
      setPrSourcecode () {
        return this.$store.getters.getAllSourcecodes.find(sc => sc && sc.code === 'PR') || {}
      },
      /**
       * Resets the component's internal state
       */
      resetForm () {
        const batch = this.setFormBatch(this.form.batch.value)
        const sourcecode = this.setPrSourcecode()
        const form = {
          batch: { label: batch ? batch.name : '', value: batch ? batch.id : '' },
          period: { label: '', value: '' },
          description: '',
          date: '',
          number: autoNumber() || '',
          debits_decimal: 0,
          credits_decimal: 0,
          confirmation_number: '',
          original_trx_number: '',
          effective_date: this.effectiveDateMandatory ? getSimpleDate(new Date(), 'yyyy-mm-dd') : '',
          source_code: this.jeType === 'je' ? { label: '', value: '' } : { label: sourcecode.name, value: sourcecode.id }
        }
        this.form = { ...form }
      },
      /**
       * Updates credits and debits in the header based on the sum of the
       * positive lines and sum of the negative lines
       */
      updateDebitsAndCredits () {
        let debits = 0
        let credits = 0
        this.lines.forEach(line => {
          if (line && line.deleteLine) return
          const amount = parseFloat(line.form.amount_decimal)
          if (amount && amount >= 0) {
            debits = debits + amount
          } else if (amount && amount < 0) {
            credits = credits + amount
          }
        })
        this.form.debits_decimal = (Math.round(debits * 100) / 100).toFixed(2)
        this.form.credits_decimal = (Math.round(credits * 100) / 100).toFixed(2)
        this.transactionIsBalanced = isJeBalanced(this)
        this.updateForm(this.form)
      },
      /**
       * Updates the header form
       */
      updateForm (form, changedField = null) {
        this.form = Object.assign({}, form)
        if (changedField === 'batch') {
          this.setBatchAttributeLineValues(this.batchAttributes)
        } else if (changedField === 'number') {
          this.journalNumberVerifier()
        }
        const b = this.fields.batch.inputFields
        const t = this.fields.jeData.inputFields
        this.trackHeaderChanges()
        this.isSaveable = checkIfRequirementsMet(this.form, [...b, ...t]) && !!this.lines.length
        this.isOpenable = this.checkIfJeIsOpenable()
      },
      /**
       * Updates distribution lines
       * @param lines
       */
      updateLines (lines) {
        this.lines = lines
        this.updateDebitsAndCredits()
        this.isOpenable = this.checkIfJeIsOpenable()
      },
      /**
       * Returns whether or not JE can be opened
       * @return boolean
       */
      checkIfJeIsOpenable () {
        const { isSaveable, transactionIsBalanced, lines } = this
        return (isSaveable && transactionIsBalanced && (lines && lines.length > 0))
      },

      /**
       * Tracks changes to the vendor
       */
      journalNumberVerifier () {
        this.trxAlreadyExists = false
        if (this.trxAlreadyExistsMsg) this.trxAlreadyExistsMsg.close()
        this.vendorNumberCheckTimeout = setTimeout(() => this.checkIfTrxExists(), 1000)
      }
    },
    computed: {
      ...mapGetters({
        getPoLines: 'getPoLines',
        checkIfUserHasRequiredPermissions: 'checkIfUserHasRequiredPermissions'
      }),
      hasCreatePermission () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_payrollentry', 'entry.add_payrollentry'])
      },
      hasEditPermission () {
        if (!this.hasCreatePermission) return false
        else return true
      },
      userCanCreateBatch () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_batch', 'entry.add_batch'])
      },
      hasPermission () {
        return {
          create: this.hasCreatePermission,
          edit: this.hasEditPermission,
          delete: true,
          post: true
        }
      },
      fields () {
        let fields = {
          batch: {
            inputFields: [
              {
                name: 'batch',
                label: 'Batch Name',
                dataType: 'number',
                fieldType: 'input',
                autocomplete: true,
                searchAsync: true,
                queryData: { endpoint: 'batches' },
                required: true,
                triggerOnFocus: false,
                msg: 'Please select a batch',
                addBtn: this.userCanCreateBatch
              }
            ],
            displayFields: [
              {
                name: 'batch',
                label: 'Company',
                value: this.batchAttributes.company ? this.batchAttributes.company.name : ''
              },
              {
                name: 'batch',
                label: 'Production',
                value: this.batchAttributes.production ? this.batchAttributes.production.name : ''
              },
              {
                name: 'batch',
                label: 'Bank',
                value: this.batchAttributes.bank ? this.batchAttributes.bank.name : ''
              },
              {
                name: 'batch',
                label: 'Currency',
                value: this.batchAttributes.currency ? this.batchAttributes.currency.code : ''
              }
            ]
          },
          jeData: {
            inputFields: [
              {
                name: 'description',
                label: 'Description',
                dataType: 'string',
                fieldType: 'input',
                required: true,
                msg: 'Please provide a description',
                min: 3
              },
              {
                name: 'number',
                label: this.$route.name === 'NewJe' ? 'Journal Number' : 'Payroll Number',
                dataType: 'string',
                fieldType: 'input',
                required: true
              },
              {
                name: 'date',
                label: this.$route.name === 'NewJe' ? 'Date' : 'Week Ending',
                dataType: 'string',
                fieldType: 'date',
                required: true,
                msg: 'Please specify a date'
              },
              {
                name: 'amount_decimal',
                label: 'Amount',
                dataType: 'string',
                fieldType: 'money',
                msg: 'Please specify amount'
              },
              {
                name: 'effective_date',
                label: 'Effective Date',
                dataType: 'string',
                required: this.effectiveDateMandatory,
                fieldType: 'date'
              },
              {
                name: 'confirmation_number',
                label: 'Confirmation Number',
                dataType: 'string',
                fieldType: 'input'
              },
              {
                name: 'original_trx_number',
                label: 'Original TRX Number',
                dataType: 'string',
                fieldType: 'input'
              },
              {
                name: 'source_code',
                label: 'Source code',
                dataType: 'number',
                fieldType: 'input',
                autocomplete: true,
                searchAsync: true,
                queryData: { endpoint: 'sourcecodes' },
                triggerOnFocus: false,
                disabled: (this.$route.name === 'NewPr')
              },
              {
                name: 'period',
                label: 'Period',
                dataType: 'number',
                fieldType: 'select',
                remote: false,
                options: [
                  { label: 'Next', value: '', selected: !(this.currentPeriod && this.currentPeriod.id) }
                ]
              }
            ]
          },
          jeInfo: {
            inputFields: [
              {
                name: 'credits_decimal',
                label: 'Credits',
                dataType: 'string',
                fieldType: 'money',
                disabled: true,
                isMoney: true
              },
              {
                name: 'debits_decimal',
                label: 'Debits',
                dataType: 'string',
                fieldType: 'money',
                disabled: true,
                isMoney: true
              }
            ],
            existingModelFields: [
              {
                label: 'Transaction #',
                value: ''
              },
              {
                label: 'Date posted',
                value: ''
              },
              {
                label: 'Status',
                value: 'NEW'
              }
            ]
          }
        }
        if (this.currentPeriod && this.currentPeriod.id) {
          fields.jeData.inputFields[8].options.push({
            label: 'Current',
            value: this.currentPeriod.id,
            selected: true
          })
        }
        return fields
      }
    },
    async beforeRouteLeave (to, from, next) {
      try {
        await store.dispatch('clearPoState', true)
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    },
    beforeDestroy () {
      this.$store.dispatch('clearHeaderFormState')
    }
  }
</script>

<style scoped>
  .h-padded {
    padding: 0 12px;
  }
  .v-padded-s {
    padding-top: 12px;
    padding-bottom: 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-form-item__label {
    padding: 20px !important;
    border: 1px solid pink;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Prs/New.vue